<template>
  <span :class="badgeClasses">
    <span v-if="dot" :class="dotClasses" />
    <slot>{{ label }}</slot>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue"

type BadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "purple"
  | "blue"

interface Props {
  variant?: BadgeVariant
  label?: string
  dot?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: "neutral",
  label: undefined,
  dot: false,
})

const variantClasses: Record<BadgeVariant, string> = {
  success: "bg-green-50 text-green-600",
  warning: "bg-amber-50 text-amber-600",
  error: "bg-red-50 text-red-600",
  info: "bg-navy-50 text-navy-600",
  neutral: "bg-neutral-100 text-neutral-700",
  purple: "bg-violet-50 text-violet-700",
  blue: "bg-blue-50 text-blue-700",
}

const dotColorClasses: Record<BadgeVariant, string> = {
  success: "bg-green-500",
  warning: "bg-amber-400",
  error: "bg-red-500",
  info: "bg-navy-500",
  neutral: "bg-neutral-400",
  purple: "bg-violet-500",
  blue: "bg-blue-500",
}

const badgeClasses = computed(() => [
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize",
  props.dot ? "gap-1.5" : "",
  variantClasses[props.variant],
])

const dotClasses = computed(() => [
  "h-[7px] w-[7px] rounded-full",
  dotColorClasses[props.variant],
])
</script>
